<template>
	<div v-if="visible" class="history-overlay" @click.self="handleClose">
		<div class="history-container" ref="historyContainer">
			<!-- 关闭按钮 -->
			<button class="close-btn" @click="handleClose">
				<i class="el-icon-close"></i>
			</button>
			
		<!-- 标题区域 -->
		<div class="history-header">
			<h2 class="history-title">{{ $t('Financial.records') || '历史记录' }}</h2>
		</div>
			
			<!-- 加载状态 -->
			<div v-if="loading" class="loading-container">
				<i class="el-icon-loading" style="font-size: 32px; color: var(--primary-color, #0156DC);"></i>
				<div style="margin-top: 10px;">{{ $t('common.loading') || '加载中...' }}</div>
			</div>
			
			<!-- 历史记录列表 -->
			<div v-else-if="historyList.length > 0" class="history-list-wrapper">
				<div class="history-list">
					<div 
						v-for="(item, index) in historyList" 
						:key="`history-${index}-${item.id || item.createTime}`"
						class="history-item"
					>
						<!-- 产品标题 -->
						<div class="product-title">
							{{ item.smartName || item.productName || item.name || '理财产品' }}
						</div>
						
						<!-- 主要内容 - 左右两列布局 -->
						<div class="item-content">
							<!-- 左侧信息 -->
							<div class="content-left">
								<div class="info-item">
									<div class="info-value">{{ formatAmount(item.amount) }}</div>
									<div class="info-label">{{ $t('Financial.amount') || '金额' }}</div>
								</div>
								<div class="info-item">
									<div class="info-value">{{ item.allDay || item.days || 0 }} {{ $t('Financial.days') || '天' }}</div>
									<div class="info-label">{{ $t('Financial.investmentPeriod') || '投资周期' }}</div>
								</div>
								<div class="info-item">
									<div class="info-value">{{ item.rate || 0 }}%</div>
									<div class="info-label">{{ $t('Financial.dailyRate') || '日利率' }}</div>
								</div>
							</div>
							
							<!-- 右侧信息 -->
							<div class="content-right">
								<div class="info-item">
									<div class="info-value profit-value">{{ formatAmount(calculateProfit(item)) }}</div>
									<div class="info-label">{{ $t('Financial.currentProfit') || '当期利润' }}</div>
								</div>
								<div class="info-item">
									<div class="info-value purchase-time">{{ formatTime(item.buyTime || item.createTime || item.time) }}</div>
									<div class="info-label">{{ $t('Financial.purchaseTime') || '购买时间' }}</div>
								</div>
								<div class="info-item">
									<div :class="['status-badge', getStatusClass(item.status)]">
										{{ getStatusText(item.status) }}
									</div>
									<div class="info-label">{{ $t('Financial.status') || '状态' }}</div>
								</div>
							</div>
						</div>
						
						<!-- 操作按钮 -->
						<div class="item-actions">
							<button class="reinvest-btn" @click="handleReinvest(item)">
								{{ $t('Financial.reinvest') || '继续投资' }}
							</button>
						</div>
					</div>
				</div>
				
				<!-- 加载更多提示 -->
				<div v-if="loadingMore" class="loading-more">
					<i class="el-icon-loading"></i>
					<span>{{ $t('common.loadingMore') || '加载更多中...' }}</span>
				</div>
				
				<!-- 没有更多数据提示 -->
				<div v-else-if="!hasMore && historyList.length > 0" class="no-more">
					{{ $t('common.noMore') || '没有更多数据了' }}
				</div>
			</div>
			
			<!-- 空状态 -->
			<div v-else class="empty-state">
				<div class="empty-icon">📋</div>
				<div class="empty-text">{{ $t('Financial.noRecords') || '暂无历史记录' }}</div>
			</div>
		</div>
	</div>
</template>

<script>
export default {
	name: 'FinancialHistory',
	props: {
		visible: {
			type: Boolean,
			default: false
		}
	},
	data() {
		return {
			loading: false,
			loadingMore: false,  // 加载更多状态
			historyList: [],
			pageNo: 1,           // 当前页码
			pageSize: 10,        // 每页条数
			hasMore: true        // 是否还有更多数据
		};
	},
	mounted() {
		// 组件挂载时，如果已经是可见状态，立即加载数据
		console.log('✅ 历史记录组件挂载，visible:', this.visible);
		if (this.visible) {
			console.log('🔄 开始加载历史记录数据...');
			this.loadHistory();
		}
		
		// 添加滚动事件监听
		this.$nextTick(() => {
			const container = this.$refs.historyContainer;
			if (container) {
				container.addEventListener('scroll', this.handleScroll);
			}
		});
	},
	beforeDestroy() {
		// 移除滚动事件监听
		const container = this.$refs.historyContainer;
		if (container) {
			container.removeEventListener('scroll', this.handleScroll);
		}
	},
	watch: {
		visible(newVal) {
			console.log('👀 visible 变化:', newVal);
			if (newVal) {
				console.log('🔄 visible 为 true，加载数据...');
				// 重置并重新加载
				this.loadHistory(true);
			} else {
				// 关闭时重置状态，为下次打开做准备
				this.pageNo = 1;
				this.hasMore = true;
			}
		}
	},
	methods: {
		handleClose() {
			this.$emit('update:visible', false);
			this.$emit('close');
		},
		
		async loadHistory(reset = true) {
			try {
				// reset 为 true 时重置列表，false 时追加
				if (reset) {
					this.loading = true;
					this.pageNo = 1;
					this.historyList = [];
					this.hasMore = true;
				} else {
					this.loadingMore = true;
				}
				
				console.log(`🔄 加载第 ${this.pageNo} 页数据...`);
				
				// 调用API获取历史记录
				const response = await this.$http.get('/swx/customer/order/listSmartOrder', {
					params: {
						pageNo: this.pageNo,
						pageSize: this.pageSize
					}
				});
				
				console.log('📜 理财历史记录响应:', response);
				
				if (response && response.code === 200) {
					let newRecords = [];
					
					// 根据实际 API 响应结构处理数据
					if (response.result && response.result.records) {
						newRecords = response.result.records;
					} else if (response.result && Array.isArray(response.result)) {
						newRecords = response.result;
					} else if (Array.isArray(response.data)) {
						newRecords = response.data;
					}
					
					// 追加或替换数据
					if (reset) {
						this.historyList = newRecords;
					} else {
						this.historyList = [...this.historyList, ...newRecords];
					}
					
					// 判断是否还有更多数据
					if (newRecords.length < this.pageSize) {
						this.hasMore = false;
						console.log('✅ 已加载全部数据');
					} else {
						this.hasMore = true;
					}
					
					console.log(`📊 第 ${this.pageNo} 页加载完成，共 ${newRecords.length} 条，总计 ${this.historyList.length} 条`);
				} else {
					if (reset) {
						this.historyList = [];
					}
					this.hasMore = false;
					if (reset) {
						this.$toast.warning(this.$t('Financial.noRecords') || '暂无历史记录');
					}
				}
			} catch (error) {
				console.error('❌ 加载理财历史记录失败:', error);
				this.$toast.error(this.$t('Financial.loadError') || '加载失败');
				if (reset) {
					this.historyList = [];
				}
				this.hasMore = false;
			} finally {
				this.loading = false;
				this.loadingMore = false;
			}
		},
		
		getStatusClass(status) {
			// 将状态转换为数字（以防是字符串）
			const statusNum = parseInt(status);
			// status = 0: 进行中, status = 1: 已完成 (与手机端保持一致)
			const statusMap = {
				0: 'active',      // 进行中 - 绿色
				1: 'completed',   // 已完成 - 灰色
				2: 'cancelled',   // 已取消 - 红色
				3: 'pending'      // 待处理 - 橙色
			};
			return statusMap[statusNum] || 'active';
		},
		
		getStatusText(status) {
			// 将状态转换为数字（以防是字符串）
			const statusNum = parseInt(status);
			// status = 0: 进行中, status = 1: 已完成 (与手机端保持一致)
			const statusMap = {
				0: this.$t('Financial.statusActive') || '进行中',
				1: this.$t('Financial.statusCompleted') || '已完成',
				2: this.$t('Financial.statusCancelled') || '已取消',
				3: this.$t('Financial.statusPending') || '待处理'
			};
			return statusMap[statusNum] || this.$t('Financial.statusUnknown') || '未知';
		},
		
		// 刷新历史记录
		handleRefresh() {
			this.loadHistory(true);  // 重置并重新加载
		},
		
		// 处理滚动事件
		handleScroll(event) {
			const container = event.target;
			const scrollTop = container.scrollTop;
			const scrollHeight = container.scrollHeight;
			const clientHeight = container.clientHeight;
			
			// 距离底部 100px 时触发加载
			const threshold = 100;
			const distanceToBottom = scrollHeight - scrollTop - clientHeight;
			
			// console.log('📏 滚动位置:', { scrollTop, scrollHeight, clientHeight, distanceToBottom });
			
			// 如果接近底部且没有在加载且还有更多数据
			if (distanceToBottom < threshold && !this.loadingMore && !this.loading && this.hasMore) {
				console.log('🔄 触发加载更多...');
				this.loadMore();
			}
		},
		
		// 加载更多
		async loadMore() {
			if (this.loadingMore || !this.hasMore) {
				return;
			}
			
			console.log('📄 加载下一页...');
			this.pageNo++;  // 增加页码
			await this.loadHistory(false);  // 追加模式加载
		},
		
		// 格式化金额
		formatAmount(amount) {
			if (amount === undefined || amount === null) {
				return '0.00';
			}
			const num = parseFloat(amount);
			if (isNaN(num)) {
				return '0.00';
			}
			return num.toFixed(2);
		},
		
		// 格式化时间
		formatTime(time) {
			if (!time) {
				return '-';
			}
			// 如果是时间戳
			if (typeof time === 'number') {
				const date = new Date(time);
				return date.toLocaleString('zh-CN', {
					year: 'numeric',
					month: '2-digit',
					day: '2-digit',
					hour: '2-digit',
					minute: '2-digit'
				});
			}
			// 如果已经是格式化的字符串，直接返回
			return time;
		},
		
		// 计算利润
		calculateProfit(item) {
			// 优先使用API返回的利润字段
			if (item.currentProfit !== undefined && item.currentProfit !== null) {
				return item.currentProfit;
			}
			if (item.allProfit !== undefined && item.allProfit !== null) {
				return item.allProfit;
			}
			if (item.profit !== undefined && item.profit !== null) {
				return item.profit;
			}
			
			// 如果没有，则计算：金额 * 利率 * 天数 / 100
			if (item.amount && item.rate && item.allDay) {
				return (item.amount * item.rate * item.allDay / 100).toFixed(2);
			}
			
			return '0.00';
		},
		
		// 继续投资
		handleReinvest(item) {
			console.log('🔄 继续投资:', item);
			// 关闭历史记录弹窗
			this.handleClose();
			// 可以emit事件通知父组件，或者直接跳转
			this.$emit('reinvest', item);
		}
	}
};
</script>

<style scoped>
/* 遮罩层 */
.history-overlay {
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background: var(--modal-overlay-bg, rgba(0, 0, 0, 0.6));
	display: flex;
	align-items: center;
	justify-content: center;
	z-index: 10000;
	transition: all 0.3s ease;
}

/* 容器 */
.history-container {
	width: 700px;
	max-width: 95vw;
	max-height: 90vh;
	background: var(--card-bg, #ffffff);
	border-radius: 16px;
	box-shadow: var(--modal-shadow, 0 8px 32px rgba(0, 0, 0, 0.15));
	overflow-y: auto;
	position: relative;
	padding: 40px;
	transition: background-color 0.3s ease, box-shadow 0.3s ease;
}

/* 关闭按钮 */
.close-btn {
	position: absolute;
	top: 20px;
	right: 20px;
	width: 32px;
	height: 32px;
	display: flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;
	border-radius: 50%;
	background: var(--bg-secondary, #f5f5f5);
	color: var(--text-secondary, #666);
	border: none;
	transition: all 0.3s ease;
	z-index: 10;
}

.close-btn:hover {
	background: var(--hover-bg, rgba(0, 0, 0, 0.1));
	color: var(--text-primary, #333);
}

/* 标题区域 */
.history-header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 30px;
}

.history-title {
	font-size: 24px;
	font-weight: 600;
	color: var(--text-primary, #333);
	margin: 0;
	transition: color 0.3s ease;
}

.refresh-btn {
	display: flex;
	align-items: center;
	gap: 6px;
	padding: 8px 16px;
	background: var(--bg-secondary, #f5f5f5);
	border: 1px solid var(--border-color, #dcdfe6);
	border-radius: 6px;
	color: var(--text-primary, #333);
	font-size: 14px;
	cursor: pointer;
	transition: all 0.3s ease;
}

.refresh-btn:hover:not(:disabled) {
	background: var(--hover-bg, rgba(1, 102, 252, 0.1));
	border-color: var(--primary-color, #0156DC);
	color: var(--primary-color, #0156DC);
}

.refresh-btn:disabled {
	opacity: 0.6;
	cursor: not-allowed;
}

.refresh-btn i {
	font-size: 16px;
}

.refresh-btn .rotating {
	animation: rotate 1s linear infinite;
}

@keyframes rotate {
	from {
		transform: rotate(0deg);
	}
	to {
		transform: rotate(360deg);
	}
}

/* 加载状态 */
.loading-container {
	text-align: center;
	padding: 60px 20px;
	color: var(--text-secondary, #666);
}

/* 历史记录列表包装器 */
.history-list-wrapper {
	display: flex;
	flex-direction: column;
}

/* 历史记录列表 */
.history-list {
	display: flex;
	flex-direction: column;
	gap: 15px;
	margin-bottom: 15px;
}

.history-item {
	background: var(--bg-secondary, #f9f9f9);
	border: 1px solid var(--border-light, #e8e8e8);
	border-radius: 16px;
	padding: 20px;
	transition: all 0.3s ease;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.history-item:hover {
	transform: translateY(-2px);
	box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
	border-color: var(--primary-color, #0156DC);
}

/* 产品标题 */
.product-title {
	font-size: 18px;
	font-weight: 600;
	color: var(--text-primary, #333);
	text-align: center;
	margin-bottom: 20px;
	transition: color 0.3s ease;
}

/* 主要内容 - 左右两列布局 */
.item-content {
	display: flex;
	gap: 30px;
	margin-bottom: 20px;
	padding-bottom: 20px;
	border-bottom: 1px solid var(--border-light, #e8e8e8);
}

.content-left,
.content-right {
	flex: 1;
	display: flex;
	flex-direction: column;
	gap: 18px;
}

.info-item {
	text-align: center;
}

.info-value {
	font-size: 16px;
	font-weight: 600;
	color: var(--text-primary, #333);
	margin-bottom: 4px;
	transition: color 0.3s ease;
}

.info-value.profit-value {
	color: var(--primary-color, #0156DC);
	font-size: 18px;
}

.info-value.purchase-time {
	font-size: 14px;
	color: var(--text-secondary, #666);
	font-weight: 400;
}

.info-label {
	font-size: 13px;
	color: var(--text-tertiary, #999);
	transition: color 0.3s ease;
}

/* 状态标签 */
.status-badge {
	display: inline-block;
	padding: 4px 12px;
	border-radius: 12px;
	font-size: 14px;
	font-weight: 600;
	text-align: center;
}

.status-badge.pending {
	background: rgba(250, 140, 22, 0.1);
	color: #fa8c16;
}

.status-badge.active {
	background: var(--primary-color-10);
	color: var(--primary-color, #0156DC);
}

.status-badge.completed {
	background: rgba(153, 153, 153, 0.1);
	color: #999;
}

.status-badge.cancelled {
	background: rgba(255, 77, 79, 0.1);
	color: #ff4d4f;
}

/* 操作按钮 */
.item-actions {
	display: flex;
	justify-content: center;
}

.reinvest-btn {
	width: 100%;
	max-width: 400px;
	height: 48px;
	background: var(--primary-color, #0156DC);
	color: white;
	border: none;
	border-radius: 12px;
	font-size: 15px;
	font-weight: 600;
	cursor: pointer;
	transition: all 0.3s ease;
	box-shadow: 0 2px 8px var(--primary-color-20);
}

.reinvest-btn:hover {
	background: var(--primary-dark);
	transform: translateY(-1px);
	box-shadow: 0 4px 12px var(--primary-color-20);
}

.reinvest-btn:active {
	transform: translateY(0);
	box-shadow: 0 2px 6px var(--primary-color-20);
}

/* 加载更多提示 */
.loading-more {
	text-align: center;
	padding: 20px;
	color: var(--text-secondary, #666);
	font-size: 14px;
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 8px;
}

.loading-more i {
	font-size: 16px;
	animation: rotate 1s linear infinite;
}

/* 没有更多数据提示 */
.no-more {
	text-align: center;
	padding: 20px;
	color: var(--text-tertiary, #999);
	font-size: 14px;
	border-top: 1px solid var(--border-light, #e8e8e8);
}

/* 空状态 */
.empty-state {
	text-align: center;
	padding: 60px 20px;
	color: var(--text-secondary, #999);
}

.empty-icon {
	font-size: 64px;
	margin-bottom: 15px;
}

.empty-text {
	font-size: 16px;
	transition: color 0.3s ease;
}

/* 滚动条样式 */
.history-container::-webkit-scrollbar {
	width: 6px;
}

.history-container::-webkit-scrollbar-track {
	background: var(--bg-secondary, #f5f5f5);
	border-radius: 3px;
}

.history-container::-webkit-scrollbar-thumb {
	background: var(--border-color, #dcdfe6);
	border-radius: 3px;
}

.history-container::-webkit-scrollbar-thumb:hover {
	background: var(--text-tertiary, #999);
}

/* 响应式 */
@media (max-width: 768px) {
	.history-container {
		padding: 30px 20px;
	}
	
	.item-details {
		grid-template-columns: 1fr;
	}
}
</style>

image.png